/**
 * Shared value formatting and deep-link builders for the Activity tables and
 * detail drawers.
 *
 * Events and Sessions had each grown their own versions of all of this, and
 * they disagreed: events truncated ids as `abcdefgh...12345678` and rendered a
 * missing value as the literal string "N/A", while sessions truncated as
 * `abcdefgh…` and rendered an em dash. Same table, same page, two answers.
 */

/** Values the APIs use to mean "nothing here". */
export function isBlank(value) {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  return s === '' || s === 'N/A' || s === '—';
}

/**
 * Shorten an identifier for a table cell. Keeps the head and tail so two ids
 * that share a prefix stay distinguishable — the reason the events table chose
 * head+tail in the first place.
 */
export function truncateId(raw, { head = 8, tail = 6, max = 20 } = {}) {
  if (raw == null) return '';
  const s = String(raw);
  if (s.length <= max) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

/** Middle-truncate for long non-id text (URLs, user agents). */
export function truncateText(raw, max = 60) {
  if (raw == null) return '';
  const s = String(raw);
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

export function formatDurationSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return null;
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

export function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

/** True when a string is a URL we can safely open in a new tab. */
export function isExternalUrl(value) {
  if (typeof value !== 'string') return false;
  return /^https?:\/\//i.test(value.trim());
}

/* ------------------------------------------------------------------ links */

export function teamSlug(paramsTeam) {
  return paramsTeam || (typeof window !== 'undefined' ? localStorage.getItem('selectedTeam') : '') || '';
}

/**
 * User detail page. This is the direct route — the sessions table used to link
 * to `/activity/users?userId=…`, which lands on the *list* and relies on an
 * effect to find the row and redirect. That silently does nothing when the user
 * isn't inside the currently loaded page of results.
 */
export function userDetailPath(team, distinctId) {
  if (!distinctId) return null;
  return `/${encodeURIComponent(teamSlug(team))}/activity/users/${encodeURIComponent(distinctId)}`;
}

/** Sessions list, deep-linked to open one session's drawer. */
export function sessionDetailPath(team, sessionId) {
  if (!sessionId) return null;
  return `/${encodeURIComponent(teamSlug(team))}/activity/sessions?sessionId=${encodeURIComponent(sessionId)}`;
}
